/**
 * Feature E06-F02 — Research run orchestrator typed api-client + Query/mutation
 * hooks (web side). Drives the Run/Progress screen (`ResearchRunView`).
 *
 * Feature-owned (NOT in the shared queries.ts / queryKeys.ts). Uses the shared
 * `apiClient` (auto-injects the session Bearer token, normalizes the response,
 * never throws) + `unwrap` (the single throw-at-the-Query-boundary helper).
 * Contracts come from `@eer/shared` (research-orchestrator.ts) so the SPA speaks
 * the exact same shapes the Fastify routes validate (routes/research-orchestrator.ts).
 *
 * Endpoints (routes/research-orchestrator.ts, mounted under /research):
 *   POST /research/:requestId/start   → research_start (normalize → job → enqueue) [T03]
 *   GET  /research/:requestId/status  → { job, topPartial } polling snapshot       [T05]
 *   POST /research/:requestId/cancel  → research_cancel (terminal)                 [T06]
 *   POST /research/:requestId/retry   → research_retry (whole job OR one company)  [T06]
 *
 * Progress transport choice — POLLING, not SSE:
 *   The backend offers BOTH a live SSE stream (GET /research/stream/:id?session_token=…)
 *   and this polling snapshot (GET /research/:id/status) which carries the SAME
 *   ProgressEvent data. The Run screen polls `/status` on a fixed cadence via
 *   TanStack Query's `refetchInterval` — no EventSource, no query-string token to
 *   manage, and it degrades cleanly behind proxies. Polling stops automatically
 *   once the job reaches a terminal/paused state (see `useResearchStatus`). A
 *   future optimisation can swap in the SSE stream for sub-second updates; the
 *   payload shape is identical so the Run screen would not change.
 */
import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  researchStatusResponseSchema,
  researchStartResponseSchema,
  researchCancelResponseSchema,
  researchRetryResponseSchema,
  isTerminalJobStatus,
  type ResearchStatusResponse,
  type ResearchStartResponse,
  type ResearchCancelResponse,
  type ResearchRetryResponse,
  type ResearchRetryInput,
} from '@eer/shared';
import { apiClient, unwrap, type ApiResultError } from '../api/client.js';

// ---------------------------------------------------------------------------
// query keys (feature-local — the shared registry has no research-run domain)
// ---------------------------------------------------------------------------

export const runKeys = {
  all: ['research', 'run'] as const,
  status: (requestId: number) => ['research', 'run', 'status', requestId] as const,
} as const;

/** Polling cadence for the status snapshot (architecture: 3-5s polling fallback). */
export const RUN_POLL_INTERVAL_MS = 3000 as const;

// ---------------------------------------------------------------------------
// fetchers / mutators
// ---------------------------------------------------------------------------

/** GET /research/:id/status — the run progress snapshot (job counters + top partial). */
export async function fetchResearchStatus(requestId: number): Promise<ResearchStatusResponse> {
  const result = await apiClient.get<unknown>(`/research/${requestId}/status`, {
    command: 'research_status',
  });
  return researchStatusResponseSchema.parse(unwrap(result));
}

/** POST /research/:id/start — kick off the run (draft → running). */
export async function startResearchRun(requestId: number): Promise<ResearchStartResponse> {
  const result = await apiClient.post<unknown>(`/research/${requestId}/start`, undefined, {
    command: 'research_start',
  });
  return researchStartResponseSchema.parse(unwrap(result));
}

/** POST /research/:id/cancel — cancel the active run (partial results survive). */
export async function cancelResearchRun(requestId: number): Promise<ResearchCancelResponse> {
  const result = await apiClient.post<unknown>(`/research/${requestId}/cancel`, undefined, {
    command: 'research_cancel',
  });
  return researchCancelResponseSchema.parse(unwrap(result));
}

/**
 * POST /research/:id/retry — retry the whole job (companyId absent) OR one failed
 * long-list row (companyId present). Both resume from where the run stopped.
 */
export async function retryResearchRun(
  requestId: number,
  input: ResearchRetryInput = {},
): Promise<ResearchRetryResponse> {
  const result = await apiClient.post<unknown>(`/research/${requestId}/retry`, input, {
    command: 'research_retry',
  });
  return researchRetryResponseSchema.parse(unwrap(result));
}

// ---------------------------------------------------------------------------
// hooks
// ---------------------------------------------------------------------------

/**
 * Poll the run status snapshot until the job reaches a terminal/paused state.
 *
 * `refetchInterval` returns `false` (stops polling) once the last snapshot's
 * `job.status` is terminal (completed/failed/cancelled) or `paused` (gated for
 * review) — no wasted requests after the run is over. Disabled for a zero/absent
 * requestId (a fresh, not-yet-created request).
 */
export function useResearchStatus(
  requestId: number,
  enabled: boolean = true,
): UseQueryResult<ResearchStatusResponse, ApiResultError> {
  return useQuery<ResearchStatusResponse, ApiResultError>({
    queryKey: runKeys.status(requestId),
    queryFn: () => fetchResearchStatus(requestId),
    enabled: enabled && requestId > 0,
    refetchInterval: (query) => {
      const status = query.state.data?.job.status;
      if (status && (isTerminalJobStatus(status) || status === 'paused')) {
        return false;
      }
      return RUN_POLL_INTERVAL_MS;
    },
  });
}

/** research_start mutation. On success primes the status query so polling begins. */
export function useStartResearchRun(
  requestId: number,
): UseMutationResult<ResearchStartResponse, ApiResultError, void> {
  const queryClient = useQueryClient();
  return useMutation<ResearchStartResponse, ApiResultError, void>({
    mutationFn: () => startResearchRun(requestId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: runKeys.status(requestId) });
    },
  });
}

/** research_cancel mutation. On success refetches the (now cancelled) snapshot. */
export function useCancelResearchRun(
  requestId: number,
): UseMutationResult<ResearchCancelResponse, ApiResultError, void> {
  const queryClient = useQueryClient();
  return useMutation<ResearchCancelResponse, ApiResultError, void>({
    mutationFn: () => cancelResearchRun(requestId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: runKeys.status(requestId) });
    },
  });
}

/**
 * research_retry mutation. Whole-job retry (default) or one failed company when
 * an input carries a `companyId`. On success resumes polling the snapshot.
 */
export function useRetryResearchRun(
  requestId: number,
): UseMutationResult<ResearchRetryResponse, ApiResultError, ResearchRetryInput | void> {
  const queryClient = useQueryClient();
  return useMutation<ResearchRetryResponse, ApiResultError, ResearchRetryInput | void>({
    mutationFn: (input) => retryResearchRun(requestId, input ?? {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: runKeys.status(requestId) });
    },
  });
}
